import { createSelector, type Draft } from '@reduxjs/toolkit';
import { logger } from '@src/utils';
import type { EffectiveMaxBalanceArgs } from '../../../../hooks';
import { type OrderFormSides, type Security, SideEnum, type WLTradingLimitsProps } from '../../../../types';
import type { WLRootState } from '../../WLOrderFormStore';
import type { SwapState } from './types';

export function getSecurityAndSide(
  swapState: SwapState | Draft<SwapState>
): { security: Security; side: SideEnum } | undefined {
  const { mode, form } = swapState;
  const securitiesBySymbol = swapState.referenceData.securities.securitiesBySymbol;
  // we do not need a security and side topup or withdraw
  if (mode === 'topup' || mode === 'withdraw') {
    return undefined;
  }
  // derive the active security based on the fromCurrency and toCurrency
  const { fromCurrency, toCurrency } = form;
  const fromToSymbol = `${fromCurrency.value?.Symbol}-${toCurrency.value?.Symbol}`;
  const toFromSymbol = `${toCurrency.value?.Symbol}-${fromCurrency.value?.Symbol}`;

  const security = securitiesBySymbol.get(fromToSymbol) ?? securitiesBySymbol.get(toFromSymbol);
  /**
   * Unable to find a security for the given fromCurrency and toCurrency.
   * If this happens, the env is seriously broken.
   */
  if (!security) {
    logger.error(new Error('Unable to find security for the given fromCurrency and toCurrency'), {
      fromCurrency: fromCurrency.value?.Symbol,
      toCurrency: toCurrency.value?.Symbol,
    });
    return undefined;
  }

  // if from is equal to quote, then we are buying
  const side = security.QuoteCurrency === fromCurrency.value?.Symbol ? SideEnum.Buy : SideEnum.Sell;
  return { security, side };
}

export const selectSecurityAndSide = createSelector(
  (state: WLRootState) => state.swap,
  swapState => getSecurityAndSide(swapState)
);

export const selectIsBlockedByLoader = createSelector(
  (state: WLRootState) => state.swap.meta.step,
  (state: WLRootState) => state.swap.requestStep,
  (step, requestStep) => {
    return step === 'locking' || step === 'requesting' || step === 'sendingEntity' || requestStep !== 'resting';
  }
);

export function getCurrenciesIncrement(swapState: SwapState | Draft<SwapState>) {
  const securityAndSide = getSecurityAndSide(swapState);
  const { form, mode } = swapState;
  const currenciesBySymbol = swapState.referenceData.currencies.currenciesBySymbol;
  if (mode === 'topup' || mode === 'withdraw') {
    const toCurrency = form.toCurrency.value?.Symbol
      ? currenciesBySymbol.get(form.toCurrency.value?.Symbol)
      : undefined;
    const fromCurrency = form.fromCurrency.value?.Symbol
      ? currenciesBySymbol.get(form.fromCurrency.value?.Symbol)
      : undefined;

    const toCurrencyDetails = toCurrency
      ? {
          minIncrement: toCurrency.MinIncrement,
          defaultIncrement: toCurrency.DefaultIncrement,
          currency: toCurrency,
        }
      : null;
    const fromCurrencyDetails = fromCurrency
      ? {
          minIncrement: fromCurrency.MinIncrement,
          defaultIncrement: fromCurrency.DefaultIncrement,
          currency: fromCurrency,
        }
      : null;

    return {
      toCurrencyDetails,
      fromCurrencyDetails,
    };
  }

  if (!securityAndSide) {
    return {
      toCurrencyDetails: null,
      fromCurrencyDetails: null,
    };
  }
  const { security } = securityAndSide;

  const isToEqualToBase = security.BaseCurrency === form.toCurrency.value?.Symbol;
  const isFromEqualToBase = security.BaseCurrency === form.fromCurrency.value?.Symbol;

  const fromMinIncrement = isFromEqualToBase
    ? security.MinSizeIncrement
    : security.MinAmtIncrement || security.MinPriceIncrement;
  const fromDefaultIncrement = isFromEqualToBase ? security.DefaultSizeIncrement : security.DefaultPriceIncrement;

  const toMinIncrement = isToEqualToBase
    ? security.MinSizeIncrement
    : security.MinAmtIncrement || security.MinPriceIncrement;
  const toDefaultIncrement = isToEqualToBase ? security.DefaultSizeIncrement : security.DefaultPriceIncrement;

  const details = {
    toCurrencyDetails: {
      minIncrement: toMinIncrement,
      defaultIncrement: toDefaultIncrement,
      currency: form.toCurrency.value,
    },
    fromCurrencyDetails: {
      minIncrement: fromMinIncrement,
      defaultIncrement: fromDefaultIncrement,
      currency: form.fromCurrency.value,
    },
  };
  return details;
}

export const selectCurrenciesIncrement = createSelector(
  (state: WLRootState) => state.swap,
  swapState => getCurrenciesIncrement(swapState)
);

// Select the currency and quantity fields that were last touched
export const selectLastTouchedFields = createSelector(
  (state: WLRootState) => state.swap.meta.lastTouched,
  (state: WLRootState) => state.swap.form,
  (lastTouched, form) => {
    const currencyField = lastTouched === 'fromQty' ? 'fromCurrency' : 'toCurrency';
    const quantityField = lastTouched;

    return {
      currencyField: { value: form[currencyField].value, key: currencyField },
      quantityField: { value: form[quantityField].value, key: quantityField },
    };
  }
);
// Select the currency and quantity fields that were not last touched
export const selectLastTouchedOppositeFields = createSelector(
  (state: WLRootState) => state.swap.meta.lastTouched,
  (state: WLRootState) => state.swap.form,
  (lastTouched, form) => {
    const quantityField = lastTouched === 'fromQty' ? 'toQty' : 'fromQty';
    const currencyField = lastTouched === 'fromQty' ? 'toCurrency' : 'fromCurrency';

    return {
      currencyField: { value: form[currencyField].value, key: currencyField },
      quantityField: { value: form[quantityField].value, key: quantityField },
    };
  }
);

export const selectEffectiveMaxBalanceArgs = createSelector(
  (state: WLRootState) => state.swap.form,
  selectLastTouchedFields,
  (state: WLRootState) => state.swap.quote,
  selectSecurityAndSide,
  (form, lastTouched, quote, securityAndSide): EffectiveMaxBalanceArgs => {
    return {
      account: form.marketAccount.value?.SourceAccountID,
      security: securityAndSide?.security,
      side: securityAndSide?.side satisfies SideEnum | undefined as unknown as OrderFormSides,
      currency: lastTouched.currencyField.value?.Symbol,
      price: quote?.BidPx || quote?.OfferPx || undefined,
    };
  }
);

export const selectSwapTradingLimitsArguments = createSelector(
  selectSecurityAndSide,
  selectLastTouchedFields,
  selectLastTouchedOppositeFields,
  (state: WLRootState) => state.swap.form.marketAccount.value?.SourceAccountID,
  (securityAndSide, lastTouched, oppositeLastTouched, marketAccount): WLTradingLimitsProps => {
    const symbol = securityAndSide?.security?.Symbol;
    // We always submit the order using base currency, so that should be driving trading limits
    const currency = securityAndSide?.security?.BaseCurrency;
    const baseQuantity =
      lastTouched.currencyField.value?.Symbol === currency
        ? lastTouched.quantityField.value
        : oppositeLastTouched.quantityField.value;
    const marketAccountName = marketAccount;

    return {
      quantity: baseQuantity,
      currency,
      symbol,
      marketAccountName,
    };
  }
);

export const selectSwapQuote = createSelector(
  (state: WLRootState) => state.swap.quote,
  quote => {
    return quote;
  }
);

export const selectSwapFormInValid = createSelector(
  (state: WLRootState) => state.swap.form.fromQty.hasInvalidValue,
  (state: WLRootState) => state.swap.form.toQty.hasInvalidValue,
  (state: WLRootState) => state.swap.form.fromCurrency.hasInvalidValue,
  (state: WLRootState) => state.swap.form.toCurrency.hasInvalidValue,
  (state: WLRootState) => state.swap.form.marketAccount.hasInvalidValue,
  (fromQtyError, toQtyError, fromCurrencyError, toCurrencyError, marketAccountError) => {
    return fromQtyError || toQtyError || fromCurrencyError || toCurrencyError || marketAccountError;
  }
);
